export interface AuthProvider {
  isAuthenticated(): boolean;
  getAuthHeaders(): Record<string, string>;
}

let authProvider: AuthProvider | null = null;

export function setAuthProvider(provider: AuthProvider | null) {
  authProvider = provider;
}

// Event dispatcher for session expiry
export const sessionExpiredEvent = new EventTarget();

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * API interceptor that handles all HTTP requests with automatic JWT token injection
 * and session expiry detection. When a 401 error is received, it dispatches a
 * session expired event that can be handled by the UI.
 */
export class ApiInterceptor {
  private static instance: ApiInterceptor;

  private constructor() {}

  public static getInstance(): ApiInterceptor {
    if (!ApiInterceptor.instance) {
      ApiInterceptor.instance = new ApiInterceptor();
    }
    return ApiInterceptor.instance;
  }

  /**
   * Make an HTTP request with automatic authentication and error handling
   */
  public async request(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    const { skipAuth = false, ...fetchOptions } = options;

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers as Record<string, string>,
    };

    // Add authentication headers if not skipped and user is authenticated
    if (!skipAuth && authProvider?.isAuthenticated()) {
      Object.assign(headers, authProvider.getAuthHeaders());
    }

    // Make the request
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Handle 401 Unauthorized - Session expired
    // Skip for /user/data (handled by ReqUserContext) to avoid login loops
    if (response.status === 401 && !url.includes('/user/data')) {
      console.warn('Session expired - dispatching session expired event');
      sessionExpiredEvent.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: {
          url,
          status: response.status,
          statusText: response.statusText
        }
      }));
    }

    return response;
  }

  /**
   * Convenience method for GET requests
   */
  public async get(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   */
  public async post(url: string, data?: any, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Convenience method for PUT requests
   */
  public async put(url: string, data?: any, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  public async delete(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  /**
   * Convenience method for PATCH requests
   */
  public async patch(url: string, data?: any, options: ApiRequestOptions = {}): Promise<Response> {
    return this.request(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// Export singleton instance
export const apiInterceptor = ApiInterceptor.getInstance();

/**
 * Monkey-patch the global fetch() to automatically add Authorization headers
 * This ensures ALL fetch requests in the app get authenticated automatically
 */
export function setupFetchInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Convert input to URL string
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    // Skip authentication for external URLs (auth backend, etc.)
    const isExternalUrl = url.startsWith('http://') || url.startsWith('https://');
    const skipAuth = (init?.headers && 'X-Skip-Auth' in init.headers) || isExternalUrl;

    // Add Authorization header if authenticated and not skipped
    if (!skipAuth && authProvider?.isAuthenticated()) {
      const authHeaders = authProvider.getAuthHeaders();

      // Merge headers
      const headers = new Headers(init?.headers);
      Object.entries(authHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });

      init = {
        ...init,
        headers,
      };
    }

    // Make the original fetch request
    const response = await originalFetch(input, init);

    // Handle 401 Unauthorized - Session expired
    // Skip for /user/data (initial auth check, handled by ReqUserContext)
    // Skip for external URLs (OTP auth service calls that may return 401 for invalid creds)
    const isExternalUrl401 = url.startsWith('http://') || url.startsWith('https://');
    if (response.status === 401 && !url.includes('/user/data') && !isExternalUrl401) {
      console.warn('Session expired (from intercepted fetch) - dispatching event');
      sessionExpiredEvent.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: {
          url,
          status: response.status,
          statusText: response.statusText
        }
      }));
    }

    return response;
  };

  console.log('[apiInterceptor] Global fetch() interceptor installed');
}

// Auto-setup on module load
setupFetchInterceptor();